/**
 * The home's definition moment (landing-definition): what counts as an
 * overdue invoice, proposed from the fields of one Xero invoice, accepted by
 * a person, then used by two answers asked in different words in different
 * tools.
 *
 * Every figure is the receivables story's (receivables-story.ts): its
 * sixteen open invoices, the seven past their due date, and what those add
 * up to, which is the "Overdue" line of the answer the home shows two bands
 * later. The rules are that answer's stated basis. The example's due date
 * moves to the sample workspace's morning, as the home's receivables answer
 * does (product-shot/receivables-shot). definition-story.test.ts holds all
 * of it to the story.
 */

import { CALDER_NOW } from "@/components/product-shot/fixture-calder";
import { INVOICES, printedPounds, RECEIVABLES_NOW } from "./receivables-story";
import { printedDate } from "./renewal-records";

const DAY = 86_400_000;

/** The receivables run was recorded on 11 August; the sample workspace's morning is 24 September. */
const CLOCK_SHIFT_DAYS = Math.round((CALDER_NOW - RECEIVABLES_NOW) / DAY);

/** A date of the recorded run, on the sample workspace's clock (yyyy-mm-dd). */
export function onWorkspaceClock(isoDate: string): string {
  return new Date(Date.parse(`${isoDate}T00:00:00Z`) + CLOCK_SHIFT_DAYS * DAY).toISOString().slice(0, 10);
}

const OVERDUE = INVOICES.filter((invoice) => invoice.status === "overdue");

/** The largest overdue invoice: the one the proposal is shown on. */
const EXAMPLE = OVERDUE.reduce((largest, invoice) => (invoice.amountDuePence > largest.amountDuePence ? invoice : largest));

/**
 * The definition as proposed: its name, its version, and its three rules,
 * each with the invoice field it reads. The rules are the receivables
 * answer's basis: a positive amount due, aged from the due date, voided and
 * deleted invoices left out.
 */
export const DEFINITION = {
  name: "Overdue invoice",
  version: 1,
  rules: [
    { id: "amount", field: "Amount due", rule: "Money is still owed" },
    { id: "due", field: "Due date", rule: "The due date has passed" },
    { id: "status", field: "Status", rule: "Not voided or deleted" },
  ],
} as const;

export type RuleId = (typeof DEFINITION.rules)[number]["id"];

/** The invoice the rules are shown on, as Xero holds it: an approved, unpaid invoice is "awaiting payment" there. */
export const EXAMPLE_INVOICE = {
  number: EXAMPLE.number,
  customer: EXAMPLE.customer,
  /** yyyy-mm-dd, on the sample workspace's clock. */
  dueDate: onWorkspaceClock(EXAMPLE.dueDate),
  fields: {
    amount: printedPounds(EXAMPLE.amountDuePence),
    due: printedDate(onWorkspaceClock(EXAMPLE.dueDate)),
    status: "Awaiting payment",
  } satisfies Record<RuleId, string>,
} as const;

/** What the definition gives on the workspace's data: the invoices it counts, of those open. */
export const ON_YOUR_DATA = { overdue: OVERDUE.length, open: INVOICES.length } as const;

/** What the overdue invoices add up to, and how many customers owe it. */
export const OVERDUE_TOTAL = printedPounds(OVERDUE.reduce((sum, invoice) => sum + invoice.amountDuePence, 0));
export const OVERDUE_CUSTOMERS = new Set(OVERDUE.map((invoice) => invoice.customer)).size;

/**
 * The same thing asked twice, in different words and different tools; both
 * answers use the accepted definition, so both give the same figure.
 */
export const ASKS = [
  { id: "twin", tool: "Twin chat", question: "How much is overdue?" },
  { id: "chatgpt", tool: "ChatGPT", question: "What are we owed that’s late?" },
] as const;

export type AskId = (typeof ASKS)[number]["id"];

/** What the motion shows, for a reader who cannot watch it. */
export const DEFINITION_SUMMARY = `An illustration of a definition proposed in the Inbox. From the amount due, due date and status of Xero invoice ${EXAMPLE_INVOICE.number}, your Twin proposes what counts as an overdue invoice: money is still owed, the due date has passed, and the invoice is not voided or deleted. On this workspace's data that is ${ON_YOUR_DATA.overdue} of ${ON_YOUR_DATA.open} open invoices. A person accepts it. The same thing is then asked in ${ASKS[0].tool} and in ${ASKS[1].tool}, in different words, and both answers give ${OVERDUE_TOTAL} and name the definition and version they used.`;
